import { useState } from 'react';
import { Play, CheckCircle2, AlertTriangle, Activity } from 'lucide-react';
import { AuditConsole } from './AuditConsole';

type SimulationPhase = 'idle' | 'detecting' | 'analyzing' | 'alerting' | 'resolved';

interface MetricDisplayProps {
    label: string;
    value: string | number;
    phase: SimulationPhase;
    icon: React.ComponentType<{ className?: string }>;
}

function MetricDisplay({ label, value, phase, icon: Icon }: MetricDisplayProps) {
    const getStatusColor = () => {
        if (phase === 'detecting' || phase === 'analyzing') return 'text-[var(--fire-orange)] border-[var(--fire-orange)]';
        if (phase === 'alerting') return 'text-red-500 border-red-500 animate-pulse';
        if (phase === 'resolved') return 'text-green-500 border-green-500';
        return 'text-gray-500 border-gray-700';
    };

    return (
        <div className={`p-4 rounded-lg border-2 transition-all duration-300 ${getStatusColor()}`}>
            <div className="flex items-center gap-2 mb-2">
                <Icon className="w-4 h-4" />
                <div className="text-xs uppercase tracking-wider opacity-70">{label}</div>
            </div>
            <div className="text-2xl font-mono font-bold">{value}</div>
        </div>
    );
}

/**
 * Interactive Compliance Demo - Phase 2 Enhancement
 * Allows users to simulate an inspection and see system response
 */
export function ComplianceDemo() {
    const [phase, setPhase] = useState<SimulationPhase>('idle');
    const [isSimulating, setIsSimulating] = useState(false);

    const wait = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

    const runSimulation = async () => {
        setIsSimulating(true);

        // Phase 1: Detecting (1s)
        setPhase('detecting');
        await wait(1000);

        // Phase 2: Analyzing (1s)
        setPhase('analyzing');
        await wait(1000);

        // Phase 3: Alerting (1.5s)
        setPhase('alerting');
        await wait(1500);

        // Phase 4: Resolved (2s)
        setPhase('resolved');
        await wait(2000);

        // Reset
        setPhase('idle');
        setIsSimulating(false);
    };

    const getMetrics = () => {
        switch (phase) {
            case 'detecting':
                return {
                    sensors: 'SCANNING...',
                    compliance: '—',
                    status: 'DETECTING',
                    incidents: '1 ANOMALY',
                };
            case 'analyzing':
                return {
                    sensors: 'ANALYZING...',
                    compliance: '—',
                    status: 'ANALYZING',
                    incidents: 'VALIDATING',
                };
            case 'alerting':
                return {
                    sensors: '⚠️ 47',
                    compliance: '⚠️ REVIEW',
                    status: 'ALERTING',
                    incidents: 'NOTIFYING',
                };
            case 'resolved':
                return {
                    sensors: '✓ 47',
                    compliance: '✓ 100%',
                    status: 'COMPLIANT',
                    incidents: '✓ RESOLVED',
                };
            default:
                return {
                    sensors: '47',
                    compliance: '94%',
                    status: 'MONITORING',
                    incidents: '0',
                };
        }
    };

    const metrics = getMetrics();

    const getBorderColor = () => {
        if (phase === 'detecting' || phase === 'analyzing') return 'border-[var(--fire-orange)] bg-orange-500/5';
        if (phase === 'alerting') return 'border-red-500 bg-red-500/10';
        if (phase === 'resolved') return 'border-green-500 bg-green-500/10';
        return 'border-gray-700 bg-gray-900/50';
    };

    const getButtonText = () => {
        if (phase === 'detecting') return 'Detecting...';
        if (phase === 'analyzing') return 'Analyzing...';
        if (phase === 'alerting') return 'Alerting...';
        if (phase === 'resolved') return 'Resolved ✓';
        return 'Simulate Inspection';
    };

    return (
        <section
            className="py-24 px-4 md:px-8 lg:px-16"
            style={{ background: 'var(--dark-bg)' }}
            aria-labelledby="demo-title"
        >
            <div className="max-w-5xl mx-auto">
                {/* Section Header */}
                <div className="text-center mb-12">
                    <h2
                        id="demo-title"
                        className="text-4xl md:text-5xl font-display font-bold text-white mb-4"
                    >
                        Live <span className="text-[var(--fire-orange)]">Audit Simulation</span>
                    </h2>
                    <p className="text-gray-400 text-lg max-w-2xl mx-auto">
                        Experience how Environaise analyzes designs and detects compliance issues in real-time
                    </p>
                </div>

                {/* Interactive Dashboard */}
                <div
                    className={`relative rounded-2xl border-2 transition-all duration-500 p-8 ${getBorderColor()}`}
                    role="region"
                    aria-label="Interactive compliance demo"
                >
                    {/* Status Banner */}
                    <div className="flex items-center justify-between mb-8">
                        <div className="flex items-center gap-3">
                            <Activity className={`w-6 h-6 ${phase === 'idle' ? 'text-gray-500' : phase === 'resolved' ? 'text-green-500' : 'text-[var(--fire-orange)] animate-pulse'}`} />
                            <div>
                                <div className="text-xs uppercase tracking-wider text-gray-500">System Status</div>
                                <div className="text-lg font-mono font-bold text-white">{metrics.status}</div>
                            </div>
                        </div>

                        {phase === 'resolved' && (
                            <div className="flex items-center gap-2 px-4 py-2 rounded-lg bg-green-500/20 border border-green-500">
                                <CheckCircle2 className="w-5 h-5 text-green-500" />
                                <span className="text-green-500 font-semibold">All Systems Compliant</span>
                            </div>
                        )}
                    </div>

                    {/* Metrics Grid */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                        <MetricDisplay
                            label="Active Sensors"
                            value={metrics.sensors}
                            phase={phase}
                            icon={Activity}
                        />
                        <MetricDisplay
                            label="Compliance"
                            value={metrics.compliance}
                            phase={phase}
                            icon={CheckCircle2}
                        />
                        <MetricDisplay
                            label="Status"
                            value={metrics.status}
                            phase={phase}
                            icon={AlertTriangle}
                        />
                        <MetricDisplay
                            label="Incidents"
                            value={metrics.incidents}
                            phase={phase}
                            icon={Activity}
                        />
                    </div>

                    {/* Progress Bar (active during simulation) */}
                    {(phase === 'detecting' || phase === 'analyzing' || phase === 'alerting') && (
                        <div className="mb-8">
                            <div className="text-xs uppercase tracking-wider text-gray-500 mb-2">
                                Processing Inspection...
                            </div>
                            <div className="h-2 bg-gray-800 rounded-full overflow-hidden">
                                <div
                                    className="h-full bg-[var(--gradient-fire)]"
                                    style={{
                                        animation: 'progressSweep 3.5s ease-out forwards'
                                    }}
                                />
                            </div>
                        </div>
                    )}

                    {/* Visual Feedback */}
                    <div className="flex items-center justify-center py-8">
                        {phase === 'idle' && (
                            <div className="text-center text-gray-500">
                                <Play className="w-16 h-16 mx-auto mb-4 opacity-50" />
                                <p>Click below to start simulation</p>
                            </div>
                        )}
                        {phase === 'detecting' && (
                            <div className="text-center text-[var(--fire-orange )]">
                                <Activity className="w-16 h-16 mx-auto mb-4 animate-pulse" />
                                <p className="font-mono">Scanning all zones...</p>
                            </div>
                        )}
                        {phase === 'analyzing' && (
                            <div className="text-center text-[var(--fire-orange)]">
                                <Activity className="w-16 h-16 mx-auto mb-4 animate-spin" />
                                <p className="font-mono">AI validating threat level...</p>
                            </div>
                        )}
                        {phase === 'alerting' && (
                            <div className="text-center text-red-500">
                                <AlertTriangle className="w-16 h-16 mx-auto mb-4 animate-bounce" />
                                <p className="font-mono">Sending notifications...</p>
                            </div>
                        )}
                        {phase === 'resolved' && (
                            <div className="text-center text-green-500">
                                <CheckCircle2 className="w-16 h-16 mx-auto mb-4 animate-pulse" />
                                <p className="font-mono font-bold">Inspection Passed ✓</p>
                            </div>
                        )}
                    </div>

                    {/* Audit Console - Live Log Feed */}
                    <div className="mt-8">
                        <AuditConsole phase={phase} />
                    </div>
                </div>

                {/* Control Button */}
                <div className="text-center mt-8">
                    <button
                        onClick={runSimulation}
                        disabled={isSimulating}
                        className={`px-8 py-4 rounded-xl font-semibold text-lg transition-all duration-300
                            ${isSimulating
                                ? 'opacity-50 cursor-not-allowed bg-gray-700 text-gray-400'
                                : 'hover:scale-105 active:scale-95 text-white'
                            }`}
                        style={!isSimulating ? { background: 'var(--gradient-fire)' } : {}}
                        aria-label={getButtonText()}
                    >
                        {getButtonText()}
                    </button>

                    <p className="text-gray-500 mt-4 text-sm">
                        Experience how the platform handles compliance checks from detection to resolution
                    </p>
                </div>
            </div>
        </section>
    );
}

export default ComplianceDemo;
